import { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parse } from 'date-fns';
import { ru, uk } from 'date-fns/locale';

import { FieldClearButton } from '@/components/FieldClearButton';
import { GridPicker, type GridPickerItem } from '@/components/GridPicker';

import { DISPLAY_MONTH_FORMAT, MONTH_FORMAT } from '@/constants/dates';

function parseMonthPickerValue(monthValue: string): Date | undefined {
  let parsed: Date;
  try {
    parsed = parse(`${monthValue}-01`, 'yyyy-MM-dd', new Date());
  } catch {
    return undefined;
  }
  if (!isValid(parsed)) {
    return undefined;
  }
  return parsed;
}

type MonthPickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  label?: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
};

const getLocale = (lang: string) => {
  if (lang === 'uk') return uk;
  if (lang === 'ru') return ru;
  return undefined;
};

const MonthPicker = (props: MonthPickerProps) => {
  const {
    value,
    onChange,
    onClear,
    label,
    hint,
    placeholder,
    disabled,
    className,
    id,
    'aria-invalid': ariaInvalid,
  } = props;

  const { i18n } = useTranslation();
  const locale = getLocale(i18n.language);

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);

  const parsedDate = useMemo(() => {
    if (!value) return undefined;
    return parseMonthPickerValue(value);
  }, [value]);

  const [viewYear, setViewYear] = useState(() => {
    return parsedDate ? parsedDate.getFullYear() : new Date().getFullYear();
  });

  const displayValue = parsedDate
    ? format(parsedDate, DISPLAY_MONTH_FORMAT, { locale })
    : '';

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setViewYear(parsedDate?.getFullYear() ?? new Date().getFullYear());
    }
    setIsOpen(open);
  };

  const selectedMonth = parsedDate?.getMonth();
  const selectedYear = parsedDate?.getFullYear();

  const items = useMemo<GridPickerItem[]>(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const name = format(new Date(viewYear, i, 1), 'LLLL', { locale });
        return {
          key: i,
          label: name.charAt(0).toUpperCase() + name.slice(1),
          isSelected: selectedMonth === i && selectedYear === viewYear,
          onSelect: () => {
            onChange?.(format(new Date(viewYear, i, 1), MONTH_FORMAT));
            setIsOpen(false);
          },
        };
      }),
    [viewYear, locale, selectedMonth, selectedYear, onChange, setIsOpen],
  );

  const clearButton = onClear && value && !disabled && (
    <FieldClearButton onClear={onClear} />
  );

  return (
    <GridPicker
      id={pickerId}
      label={label}
      hint={hint}
      isInvalid={ariaInvalid}
      disabled={disabled}
      className={className}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      displayValue={displayValue}
      placeholder={placeholder}
      clearButton={clearButton}
      title={viewYear}
      onPrevious={() => setViewYear((y) => y - 1)}
      onNext={() => setViewYear((y) => y + 1)}
      items={items}
    />
  );
};

export { MonthPicker };
export type { MonthPickerProps };
